import { PrinterOutlined } from '@ant-design/icons';
import { Editor } from '@tinymce/tinymce-react';
import { useGetListReport } from '@vindr/services/actions/report.action';
import { ResponseGetLayouts } from '@vindr/services/apis/layout.api';
import { ResponseGetOrders } from '@vindr/services/apis/order.api';
import { ResponseGetReports } from '@vindr/services/apis/report.api';
import { ResponseFetchStudyList } from '@vindr/services/apis/study.api';
import cx from '@vindr/utils/helpers/cx';
import { Modal, ModalProps } from 'antd';
import { MutableRefObject, forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StructuredReportElement } from '@vindr/ui/src/components/common/StructuredReport/components/elements/types';
import './Print.css';
import useGenerateReport from './useGenerateReport';
import PDFViewer from '@vindr/ui/src/components/ui/PDFViewer';
import { TextEditor } from '@vindr/ui';

const PrintModal = forwardRef<
  Editor,
  {
    report?: ResponseGetReports['data'][0];
    order?: ResponseGetOrders['data'][0];
    layout?: ResponseGetLayouts['data'][0];
    study?: ResponseFetchStudyList['data'][0];
    reports?: ReturnType<typeof useGetListReport>[number]['data'][];
    structureReportSections?: StructuredReportElement[];
    handleClose: (e) => void;
    okText?: ModalProps['okText'];
    onOk?: ModalProps['onOk'];
    open?: boolean;
    className?: string;
    modalClassName?: string;
    zIndex?: number;
    pdfURL?: string;
  }
>(
  (
    {
      report,
      order,
      layout,
      study,
      reports,
      handleClose,
      structureReportSections,
      okText,
      onOk,
      open = true,
      className,
      modalClassName,
      zIndex = 1010,
      pdfURL,
    },
    ref
  ) => {
    const { t } = useTranslation('Vindoc');
    const localRef = useRef<Editor>(null);
    const iframeRef = (ref || localRef) as MutableRefObject<Editor>;
    const viewerInstance = useRef<any>(null);

    const {
      srcDoc,
      handlePrint,
      isLoading: isPrinting,
    } = useGenerateReport({
      layout,
      order,
      report,
      reports,
      structureReportSections,
      study,
    });

    return (
      <Modal
        forceRender
        aria-labelledby="max-width-dialog-title"
        cancelText={<div className="text-black">{t('Cancel')}</div>}
        className={cx('printing-dialog', modalClassName)}
        closable={false}
        okButtonProps={{ loading: isPrinting }}
        open={open}
        style={{ backgroundColor: '#fff', color: '#000', paddingBottom: 0, top: 12 }}
        width={785}
        zIndex={zIndex}
        okText={
          okText || (
            <>
              <PrinterOutlined /> {t('Print')}
            </>
          )
        }
        onCancel={handleClose}
        onOk={(e) => {
          if (typeof onOk === 'function') return onOk(e);
          if (pdfURL) {
            if (viewerInstance?.current) {
              const instance = viewerInstance?.current;
              instance.UI.useEmbeddedPrint(true);
              instance.UI.setPrintQuality(2);
              return instance.UI.printInBackground({
                includeAnnotations: false,
                includeComments: false,
              });
            }

            return handlePrint(pdfURL);
          }

          return handlePrint(iframeRef.current.editor?.iframeElement);
        }}
      >
        <div className="preview-content" style={{ position: 'relative' }}>
          {pdfURL ? (
            <div className="h-[85vh] bg-white">
              <PDFViewer ref={viewerInstance} style={{ height: '100%' }} url={pdfURL} />
            </div>
          ) : (
            <TextEditor
              ref={iframeRef}
              disabled
              className={cx('h-[85vh] w-full [&_[role="application"]]:border-none', className)}
              height={'100%'}
              initialValue={srcDoc}
              initProps={{
                height: '100%',
                menubar: false,
                statusbar: false,
                toolbar: false,
                readonly: true,
                forced_root_block: 'div',
              }}
            />
          )}
        </div>
      </Modal>
    );
  }
);

PrintModal.displayName = 'PrintModal';

export default PrintModal;
